import {
  baseKeymap,
  chainCommands,
  exitCode,
  joinDown,
  joinUp,
  lift,
  selectParentNode,
  setBlockType,
  wrapIn,
} from 'prosemirror-commands';
import { dropCursor } from 'prosemirror-dropcursor';
import { gapCursor } from 'prosemirror-gapcursor';
import { history, redo, undo } from 'prosemirror-history';
import { ellipsis, emDash, inputRules, smartQuotes, undoInputRule } from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
import { DOMParser, Node, Slice } from 'prosemirror-model';
import { Plugin } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';

import { isMacOS } from '@recruitee/user-interface';

import { ProsemirrorCommand, RtPMPlugin } from '../../editor/types';
import { DEFAULT_KEYMAP_INPUTRULE_PRIORITY } from '../../helpers/utils';
import { BasicPluginSchema } from './nodes';

export class BasicPlugin implements RtPMPlugin {
  private editorView: EditorView;
  private basePlugin = new Plugin({
    props: {
      // This is default implementation but with single newline as block separator
      clipboardTextSerializer: (s: Slice) =>
        // TODO (Fragment has textBetween method but types are wrong)
        (<Node>(<unknown>s.content)).textBetween(0, s.content.size, '\n'),
      // This is default implementation but doesnt ignore \n\n etc
      clipboardTextParser: (text, $context, plain) => {
        const textArr = text.split(/(?:\r\n?|\n)/);
        const dom = document.createElement('div');
        const childNodes = textArr.map(text => {
          const paragraph = document.createElement('p');
          paragraph.textContent = text;
          return paragraph;
        });
        childNodes.forEach(node => dom.appendChild(node));
        const parser: DOMParser =
          this.editorView.someProp('clipboardParser') ||
          this.editorView.someProp('domParser') ||
          DOMParser.fromSchema(this.editorView.state.schema);

        return parser.parseSlice(dom);
      },
    },
  });

  public afterEditorInit(editorView: EditorView) {
    this.editorView = editorView;
  }

  public getNativeEditorPlugins(schema: BasicPluginSchema) {
    return [
      history(),
      dropCursor(),
      gapCursor(),
      this.basePlugin,
      { priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY - 1, plugin: keymap(baseKeymap) },
      { priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY, plugin: keymap(this.getKeyBindings(schema)) },
      {
        priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY,
        plugin: inputRules({
          rules: [...smartQuotes, emDash, ellipsis],
        }),
      },
    ];
  }

  private getKeyBindings(schema: BasicPluginSchema) {
    const hr = schema.nodes.horizontal_rule;
    const br = schema.nodes.hard_break;
    const blockquote = schema.nodes.blockquote;
    const brCommand = chainCommands(exitCode, (state, dispatch) => {
      dispatch!(state.tr.replaceSelectionWith(br.create()).scrollIntoView());
      return true;
    });

    const keymap: Record<string, ProsemirrorCommand> = {
      ['Mod-z']: undo,
      ['Shift-Mod-z']: redo,
      ['Backspace']: undoInputRule,
      ['Alt-ArrowUp']: joinUp,
      ['Alt-ArrowDown']: joinDown,
      ['Mod-BracketLeft']: lift,
      ['Escape']: selectParentNode,
      ['Shift-Ctrl-0']: setBlockType(schema.nodes.paragraph),
      ['Mod-Enter']: brCommand,
      ['Shift-Enter']: brCommand,
      ...(!isMacOS && { ['Mod-y']: redo }),
      ...(isMacOS && { ['Ctrl-Enter']: brCommand }),
      ['Mod-_']: (state, dispatch) => {
        dispatch!(state.tr.replaceSelectionWith(hr.create()).scrollIntoView());
        return true;
      },
      // schema.nodes.blockquote
      ['Ctrl->']: wrapIn(blockquote),
      // schema.nodes.heading
      // ...Object.fromEntries(
      //   [...Array(5)]
      //     .map((_, i) => i + 1)
      //     .map(level => ['Shift-Ctrl-' + level, setBlockType(schema.nodes.heading, { level })]),
      // ),
    };

    return keymap;
  }
}
